import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button as MuiButton,
} from "@mui/material";
import { Button } from "@voicecare.ai/ui-library";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TableViewIcon from "@mui/icons-material/TableView";
import CloseIcon from "@mui/icons-material/Close";
import { uploadBatchFiles } from '../../utils/apiClient';
import { TRIAL_USER_PATIENT_LIMIT } from '../../services/billingRestrictionService';
import Icon from "./Icon/Icon";
import './FileUploader.css';
import { message } from 'antd';

// Custom XLS icon component
const XLSIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M40 0H8C6.9 0 6 0.9 6 2V62C6 63.1 6.9 64 8 64H56C57.1 64 58 63.1 58 62V18L40 0Z"
      fill="#FFFFFF"
    />
    <rect x="16" y="32" width="32" height="2" fill="#666666" />
    <rect x="16" y="38" width="32" height="2" fill="#666666" />
    <rect x="16" y="44" width="32" height="2" fill="#666666" />
    <path d="M40 0V16C40 17.1 40.9 18 42 18H58L40 0Z" fill="#DDDDDD" />
    <rect x="16" y="50" width="32" height="8" rx="2" fill="#FF4E3A" />
    <text x="24" y="56" fill="white" fontSize="8" fontFamily="Arial">
      XLS
    </text>
  </svg>
);

const FileUploader = ({
  files,
  setFiles,
  setTableData,
  tableData,
  onAddPatient,
  onSubmit,
  onError = () => {},
  usecaseName = "default",
  branch = "default",
  subUseCase = "default",
  acceptedFileTypes = {
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
    "application/vnd.ms-excel.template.macroEnabled.12": [".xltm"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [
      ".xltx",
    ],
  },
  themeMode = "dark",
  currentTheme = {},
  isTrialUser = false,
  onTrialDataLimit = () => {},
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // Theme color helpers
  const bgColor = themeMode === "dark"
    ? (currentTheme?.fileUploader?.background || "#4A4B68")
    : "#e3e2e5";
  const cardBg = themeMode === "dark"
    ? (currentTheme?.fileUploader?.cardBackground || "#3E3E5C")
    : "#d7d4e0";
  const textColor = themeMode === "dark"
    ? (currentTheme?.fileUploader?.textColor || "#fff")
    : (currentTheme?.homePage?.textColor || "#222");
  const borderColor = themeMode === "dark"
    ? (currentTheme?.fileUploader?.borderColor || "#2d284d")
    : (currentTheme?.fileUploader?.borderColor || "#e0e0e0");
  const iconColor = themeMode === "dark" ? "#ffb86c" : "#ff4e3a";
  const uploadBtnBg = themeMode === "dark" ? "#ff4e3a" : "#ff4e3a";
  const uploadBtnHover = themeMode === "dark" ? "#e6402f" : "#e6402f";

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 KB";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(0));
    return size + " " + sizes[i];
  };

  const validateFile = (file) => {
    const fileType = file.type;
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    const isAcceptedType = Object.entries(acceptedFileTypes).some(
      ([mimeType, extensions]) => {
        return fileType === mimeType || extensions.includes(fileExtension);
      }
    );

    if (!isAcceptedType) {
      onError(`File type ${fileExtension} is not supported`);
      return false;
    }
    return true;
  };

  const handleFiles = (newFiles) => {
    const filesArray = Array.from(newFiles);
    const validFiles = filesArray.filter(validateFile);
    setFiles((prevFiles) => {
      const newFiles = validFiles.filter(
        (newFile) =>
          !prevFiles.some(
            (prevFile) =>
              prevFile.name === newFile.name && prevFile.size === newFile.size
          )
      );
      return [...prevFiles, ...newFiles];
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    if (droppedFiles.length === 0) {
      onError('Please drop only Excel or CSV files.');
      return;
    }

    // Use the same approach as file input
    handleFiles(droppedFiles);
  }, [onError, acceptedFileTypes, handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    // Only set dragActive to false if we're leaving the container entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleUploadClick = () => {
    // Try to find the "Upload More" button first
    const uploadMoreInput = document.getElementById("file-input-upload-more");
    if (uploadMoreInput) {
      uploadMoreInput.click();
    } else {
      // Fallback to the main file input (for empty state)
      const mainInput = document.getElementById("file-input");
      if (mainInput) {
        mainInput.click();
      }
    }
  };

  const removeFile = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const clearAll = () => {
    setFiles([]);
  };

  const processFiles = async (uploadedFiles) => {
    setIsProcessing(true);
    try {
      // Create FormData for the upload endpoint
      const formData = new FormData();
      formData.append('branch', branch);
      formData.append('usecase', usecaseName);
      formData.append('subUsecase', subUseCase);
      
      // Append each file to FormData
      uploadedFiles.forEach(file => {
        formData.append('files', file);
      });

      // Call the new upload endpoint
      const uploadResponse = await uploadBatchFiles(formData);

      if (uploadResponse.statusCode === 'SUCCESS') {
        let uploadedData = uploadResponse.response;
        
        console.log('FileUploader - Upload response:', uploadedData);
        console.log('FileUploader - isTrialUser:', isTrialUser);
        console.log('FileUploader - Data length:', uploadedData.length);
        
        // Trial restrictions will be handled in CallAutomationTest.js where getBatches() works properly
        console.log(`🔍 TRIAL DEBUG: FileUploader processed ${uploadedData.length} calls (trial restrictions handled later)`);
        
        // Always call callback for trial users so real validation can happen in CallAutomationTest.js
        if (isTrialUser && uploadedData.length > 0) {
          console.log(`🔍 TRIAL DEBUG: FileUploader calling onTrialDataLimit callback with ${uploadedData.length} calls`);
          onTrialDataLimit(uploadedData.length, TRIAL_USER_PATIENT_LIMIT, uploadedData);
        }
        
        // Ensure all rows have unique IDs
        const uploadedDataWithIds = uploadedData.map((row, index) => ({
          ...row,
          _rowId: row._rowId || `row_${Date.now()}_${index}_${Math.random()}`
        }));
        setTableData(uploadedDataWithIds);
        setFiles(uploadedFiles);
      } else {
        // Show antd message for known error codes
        switch (uploadResponse.statusCode) {
          case 400:
          case '400':
            message.error('Bad Request');
            break;
          case 413:
          case '413':
            message.error('File size too large. (Currently the max file size is 10MB)');
            break;
          case 401:
          case '401':
            message.error('Auth Issue');
            break;
          case 500:
          case '500':
            message.error('Internal server issue');
            break;
          default:
            message.error('Upload failed: ' + (uploadResponse.message || 'Unknown error'));
        }
        onError('Upload failed: ' + (uploadResponse.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      // Try to extract status code from error.response if available
      const status = error?.response?.status;
      switch (status) {
        case 400:
          message.error('Bad Request');
          break;
        case 413:
          message.error('File size too large. (Currently the max file size is 10MB)');
          break;
        case 401:
          message.error('Auth Issue');
          break;
        case 500:
          message.error('Internal server issue');
          break;
        default:
          message.error(error.message || 'Error uploading files');
      }
      onError(error.message || 'Error uploading files');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      onError("Please upload at least one file before submitting");
      return;
    }

    if (isSubmitting) {
      return; // Prevent multiple submissions
    }

    try {
      setIsSubmitting(true);

      // Validate all files one last time
      const invalidFiles = files.filter((file) => !validateFile(file));
      if (invalidFiles.length > 0) {
        onError(
          `Some files are invalid: ${invalidFiles
            .map((f) => f.name)
            .join(", ")}`
        );
        return;
      }

      // Process all files at once
      await processFiles(files);

    } catch (error) {
      onError(error?.message || "Error submitting files");
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
         <Box
       sx={{
         m: 0,
         width: "100%",
         maxHeight: "60.5vh",
         minHeight: "60.5vh",
         height: "60.5vh",
         backgroundColor: bgColor,
         borderRadius: "8px",
         display: "flex",
         flexDirection: "column",
         position: "relative",
         p: 2,
         color: textColor,
         alignItems: 'stretch',
         overflow: 'hidden',
       }}
      className="file-uploader"
    >
      {isSubmitting && (
        <div className="loader-overlay">
          <img
            src={themeMode === "dark" ? "/icons/loader_white.gif" : "/icons/loader_black.gif"}
            alt="Loading..."
            style={{ width: "150px", height: "150px" }}
          />
        </div>
      )}
      {files.length === 0 ? (
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            transition: "opacity 0.2s ease",
            opacity: dragActive ? 0.7 : 1,
            gap: 2,
            width: "100%",
            backgroundColor: cardBg,
            border: `2px dashed ${borderColor}`,
          }}
        >
          <input
            id="file-input"
            type="file"
            multiple
            onChange={handleChange}
            accept={Object.values(acceptedFileTypes).flat().join(",")}
            style={{ display: "none" }}
          />
          <Icon path={`/icons/xls-file-icon-${themeMode}.svg`} width="64px" height="64px" />
          {/* <TableViewIcon
            sx={{
              fontSize: 64,
              color: iconColor,
              opacity: 0.9,
            }}
          /> */}
          <Typography
            sx={{
              color: textColor,
              opacity: 0.9,
              textAlign: "center",
            }}
          >
            Drag & drop excel (.xls, .xlsx, xlms, xlt )
          </Typography>
          <Typography
            sx={{
              color: textColor,
              opacity: 0.9,
              textAlign: "center",
              marginTop: -1,
            }}
          >
            files to upload
          </Typography>
          <Typography
            sx={{
              color: textColor,
              opacity: 0.7,
              my: 1,
            }}
          >
            Or
          </Typography>
          <Button
            label="Upload Files"
            icon={<UploadFileIcon />}
            onClick={handleUploadClick}
            sx={{
              backgroundColor: uploadBtnBg,
              "&:hover": { backgroundColor: uploadBtnHover },
            }}
          />
        </Box>
             ) : (
                   <Box 
            sx={{ 
              flex: 1, 
              display: "flex", 
              flexDirection: "column", 
              alignItems: 'stretch',
              width: '98%',
              border: dragActive ? `3px dashed #ff4e3a` : `1px dashed ${borderColor}`,
              borderRadius: '8px',
              backgroundColor: dragActive ? 'rgba(255, 78, 58, 0.1)' : 'rgba(255, 255, 255, 0.02)',
              transition: "all 0.3s ease",
              padding: '10px',
              position: 'relative',
              minHeight: '280px',
              overflow: 'hidden',
            }}
           onDragEnter={handleDrag}
           onDragLeave={handleDragLeave}
           onDragOver={handleDragOver}
           onDrop={handleDrop}
         >
           
           {/* Grid for file cards: always 4 per row, vertical scroll if needed */}
                       <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
                marginBottom: '8px',
                maxHeight: '380px',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: themeMode === 'dark' ? '#464666 #2d284d' : '#bdbdbd #e3e2e5',
                transition: "opacity 0.3s ease",
                opacity: dragActive ? 0.4 : 1,
                padding: '2px',
              }}
            >
                         {files.map((file, index) => (
               <Box key={index} sx={{ textAlign: "center", position: 'relative', width: '100%' }}>
                                   <Box
                    sx={{
                      background: cardBg,
                      borderRadius: "6px",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "120px",
                      border: `1px solid ${borderColor}`,
                    }}
                  >
                   <Box
                     sx={{
                       position: "absolute",
                       top: 8,
                       left: 8,
                       backgroundColor: themeMode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0,0,0,0.07)",
                       borderRadius: "4px",
                       padding: "2px 6px",
                       color: textColor,
                       fontSize: "0.75rem",
                     }}
                   >
                     {formatBytes(file.size)}
                   </Box>
                   {/* Remove button for each file, always visible in top right */}
                   <IconButton
                     onClick={() => removeFile(file)}
                     sx={{
                       position: "absolute",
                       top: 6,
                       right: 6,
                       color: uploadBtnBg,
                       backgroundColor: themeMode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0,0,0,0.07)",
                       padding: "2px",
                       minWidth: "20px",
                       minHeight: "20px",
                       zIndex: 2,
                       "&:hover": {
                         backgroundColor: themeMode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0,0,0,0.15)",
                       },
                     }}
                   >
                     <CloseIcon sx={{ fontSize: "14px" }} />
                   </IconButton>
                   {/* <XLSIcon /> */}
                   <Icon path={`/icons/xls-file-icon-${themeMode}.svg`} width="60px" height="60px" />
                 </Box>
                                   <Typography
                    sx={{
                      color: textColor,
                      fontSize: "0.75rem",
                      opacity: 0.8,
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      mt: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                   {file.name}
                 </Typography>
               </Box>
             ))}
                         {/* Upload More card */}
                           <Box
                onClick={handleUploadClick}
                sx={{
                  backgroundColor: cardBg,
                  borderRadius: "6px",
                  width: "100%",
                  height: "120px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  border: `1px solid ${borderColor}`,
                  "&:hover": {
                    backgroundColor: themeMode === "dark" ? "#464666" : "#f0f0f0",
                  },
                }}
              >
               <input
                 type="file"
                 id="file-input-upload-more"
                 multiple
                 onChange={handleChange}
                 accept={Object.values(acceptedFileTypes).flat().join(",")}
                 style={{ display: "none" }}
               />
               <Icon path={`/icons/upload-icon-${themeMode}.svg`} width="30px" height="30px" />
                               <Typography
                  sx={{
                    color: textColor,
                    fontSize: "0.75rem",
                    mt: 1,
                    opacity: 0.8,
                    lineHeight: 1.2,
                  }}
                >
                 Upload More
               </Typography>
             </Box>
          </div>
                                 <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: dragActive ? "60px" : "32px",
                mt: 1,
                padding: dragActive ? "16px 12px" : "8px 12px",
                backgroundColor: dragActive ? 'rgba(255, 78, 58, 0.15)' : 'transparent',
                borderRadius: '6px',
                width: '100%',
                boxSizing: 'border-box',
                border: dragActive ? '2px dashed #ff4e3a' : 'none',
                transition: "all 0.3s ease",
              }}
            >
                                         {dragActive && (
                <Icon path={`/icons/upload-icon-${themeMode}.svg`} width="24px" height="24px" style={{ marginRight: '8px' }} />
              )}
              <Typography
                sx={{
                  color: dragActive ? '#ff4e3a' : textColor,
                  opacity: dragActive ? 1 : 0.6,
                  textAlign: "center",
                  fontSize: dragActive ? "1rem" : "0.8rem",
                  fontWeight: dragActive ? 600 : 400,
                  transition: "all 0.3s ease",
                }}
              >
                {dragActive ? "Drop files here to add more" : "Drag & drop excel files (.xls, .xlsx, xlms, xlt ) to upload"}
              </Typography>
           </Box>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 2,
          alignItems: "center",
          width: "100%",
        }}
      >
        {/*TODO: ENABLE THIS ONCE ADD PATIENT FUNCTION IS ADDED */}
        {/* <MuiButton
          variant="contained"
          onClick={onAddPatient}
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#0A0B2E",
            color: "#fff",
            textTransform: "none",
            fontSize: "0.875rem",
            py: 1,
            px: 2,
            "&:hover": {
              backgroundColor: "#161832",
            },
          }}
        >
          + Add Patient
        </MuiButton> */}

        {files.length > 0 && (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              label={"Clear All"}
              onClick={clearAll}
              disabled={isSubmitting}
              color={themeMode === "dark" ? "#fff" : "#000"}
              backgroundColor={themeMode === "dark" ? "#0A0B2E" : "#d7d4e0"}
            />
            <Button
              label={isSubmitting ? "Submitting..." : "Submit"}
              onClick={handleSubmit}
              disabled={files.length === 0 || isSubmitting}
              sx={{
                backgroundColor: uploadBtnBg,
                fontSize: "0.875rem",
                py: 1,
                px: 3,
                opacity: isSubmitting ? 0.7 : 1,
                color: "#fff",
                "&:hover": {
                  backgroundColor: uploadBtnHover,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FileUploader;